import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Divider,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import type { SystemStyleObject } from '@mui/system';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { DataGridPremium } from '@mui/x-data-grid-premium';
import type {
  GridColDef,
  GridInitialState,
  GridSortDirection,
} from '@mui/x-data-grid-premium';
import { skipToken } from '@reduxjs/toolkit/query';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { SearchBar } from '@/components/common/SearchBar';
import WidgetHeader from '@/components/common/WidgetHeader';
import ClientDetailsFlyout from '@/components/flyouts/ClientDetailsFlyout';
import { WIDGET_GRID_HEIGHT } from '@/constants/grid';
import { GRID_KEYS } from '@/constants/gridKeys';
import { useGlobalSnackbar } from '@/contexts/snackContext';
import { useAuthIsLoaded } from '@/hooks/useAuthIsLoaded';
import useClientActionsMenu from '@/hooks/useClientActionsMenu';
import type { ClientActionKey } from '@/hooks/useClientActionsMenu';
import { useGridColumns } from '@/hooks/useGridColumns';
import { useLocalization } from '@/hooks/useLocalization';
import { useWidgetWidthIsBelow } from '@/hooks/useWidgetWidthIsBelow';
import type { StandardWidgetProps } from '@/models/WidgetModels';
import { useClientColumns } from '@/modules/clientServices/hooks/useClientColumns';
import { useGetAdvisorInvestorsQuery } from '@/store/advisorInvestorsApi';
import type { AdvisorInvestorsResponse } from '@/store/advisorInvestorsApi';
import type { AdvisorInvestor } from '@/store/usersApi';
import { formatNumberToCurrency } from '@/utils/currency';
import { ContentContainer } from '@/widgets/contentContainer';
import {
  hasOrionClient,
  selectDashboardClientColumns,
} from './dashboardClientColumns';
import {
  clientTableWidths,
  createClientGridTextMeasurer,
  fitClientColumnWidths,
  HEADER_FONT_SIZE_PX,
  HEADER_HEIGHT_PX,
  HEADER_LINE_HEIGHT,
  HEADER_TITLE_GAP_PX,
  selectClientTableLayout,
  SORT_ICON_WIDTH_PX,
  TABLE_WIDTH_SLACK_PX,
} from './dashboardClientColumnWidths';
import {
  useDashboardClientCardColumn,
  type MenuForInvestorComponent,
} from './useDashboardClientCardColumn';

const EditInvestorForm = React.lazy(
  async () =>
    import('@/components/flyouts/ClientDetailsFlyout/EditInvestorForm'),
);

/**
 * Row actions the full Clients page offers and this widget does not. Access is
 * appended in table mode only: the card list drops the pinned Access column, so
 * there the menu is the only place left to start impersonation.
 */
const HIDDEN_ROW_ACTIONS: ClientActionKey[] = [
  'inviteUser',
  'invitationHistory',
  'deleteUser',
  'viewUserProfile',
  'exportUserProfile',
];

const GRID_SORTING_ORDER: GridSortDirection[] = ['asc', 'desc', null];

/**
 * The request the Clients page makes at rest, first page and Net Worth
 * descending with no limit, plus a server-side sum of AUM. The server sums
 * every matching client before paging, so the AUM headline is the advisor's
 * total rather than the sum of the loaded page. The sum gives the widget its
 * own cache entry, but every AdvisorInvestorsList invalidation still reaches
 * it.
 */
const PAGE_CLIENTS_QUERY = {
  page: 1,
  sort: [{ Property: 'NetWorth', Direction: 'desc' as const, Order: 1 }],
  sum: ['Home'],
};

/**
 * The advisor's AUM from the response's summed values. An advisor with no
 * clients gets no SummedValues from the server, which reads as zero; a
 * non-empty result without the sum is unknown.
 */
const selectTotalAum = (
  response: AdvisorInvestorsResponse | undefined,
): number | null => {
  if (response === undefined) return null;
  const summed = response.SummedValues?.find(
    (value) => value.Property === 'Home',
  );
  if (summed !== undefined) return summed.Value;
  return response.TotalLength === 0 ? 0 : null;
};

/**
 * Same pinning as the Clients page. The widget picks a layout that fits the
 * measured tile, so this only matters in a container it does not measure, such
 * as a page, where the table can still scroll sideways.
 */
const PINNED_ROW_ACTIONS: GridInitialState = {
  pinnedColumns: { right: ['access', 'actions'] },
};

const HEADER_FONT_WEIGHT = 500;

const WRAPPED_HEADER_CLASS = 'dashboard-clients-wrapped-header';

/**
 * Column sizing for a tile. Only Name and Username flex: MUI splits slack
 * equally across flex columns, so a flexing Net Worth grows as much as Name
 * does while showing "$0.00". The number and date columns are fixed at the
 * width of their widest loaded value (see fitClientColumnWidths). The two text
 * columns absorb the rest. While the tile fits it, their floor is their widest
 * loaded value, so no name truncates. A narrower tile drops them to the floors
 * here and the cells truncate with an ellipsis. A tile narrower than that sum
 * renders cards. The table never scrolls sideways. The widths here are also
 * the fallback for when canvas measurement is unavailable. Access and the
 * kebab lose the slack the page gives them. These live here, not in
 * useClientColumns, so the full-width Clients page keeps its own sizing.
 */
const COLUMN_SIZING: Partial<Record<string, Partial<GridColDef>>> = {
  Name: { flex: 1, minWidth: 120 },
  UserName: { flex: 1, minWidth: 110 },
  NetWorth: { flex: 0, width: 115, headerClassName: WRAPPED_HEADER_CLASS },
  Home: { flex: 0, width: 115, headerClassName: WRAPPED_HEADER_CLASS },
  HomePercentage: { flex: 0, width: 75, headerClassName: WRAPPED_HEADER_CLASS },
  LastLogin: { flex: 0, width: 100, headerClassName: WRAPPED_HEADER_CLASS },
  LastOrionLogin: {
    flex: 0,
    width: 100,
    headerClassName: WRAPPED_HEADER_CLASS,
  },
  access: { width: 72, headerName: '', renderHeader: undefined },
  actions: { width: 40 },
};

/**
 * The widget fills the tile it sits in, and WIDGET_GRID_HEIGHT is the floor
 * rather than the height: a taller tile shows more rows, a shorter one still
 * gets 400px, and the grid scrolls inside either way.
 *
 * ContentContainer renders a bare Box and drops `sx` for container='page' and
 * container='flyout', so the same layout is repeated on an inner element. That
 * inner element is what gives the grid its height basis in those two cases.
 */
const FILL_SX: SystemStyleObject<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  flexGrow: 1,
  height: '100%',
  minHeight: WIDGET_GRID_HEIGHT,
};

/**
 * The card list carries its own definite height (CARD_ROWS_SX), so the tile
 * has no floor: collapsing the Client list accordion shrinks the tile to the
 * header, AUM and the collapsed bar instead of leaving 400px of empty space.
 */
const CARD_FILL_SX: SystemStyleObject<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  flexGrow: 1,
  height: '100%',
};

/**
 * The tile clips its content. The card section pulls itself to the tile edges
 * (CARD_SECTION_SX), so without this its square corners poke past the rounded
 * frame. Only the tile clips: the inner layout box must not, because the card
 * section's negative margins reach outside it.
 */
const TILE_SX: SxProps<Theme> = { ...FILL_SX, overflow: 'hidden' };

const CARD_TILE_SX: SxProps<Theme> = { ...CARD_FILL_SX, overflow: 'hidden' };

const GRID_WRAPPER_SX: SxProps<Theme> = { flex: 1, minHeight: 0, mt: 2 };

const TABLE_SEARCH_SX: SxProps<Theme> = { mt: 2, maxWidth: 360 };

/**
 * Flatter than the Clients page grid: no header band, no column separators,
 * and 8px horizontal padding instead of 16px, because in a tile eight columns
 * of padding is a column's worth of width.
 */
const GRID_SX: SxProps<Theme> = {
  border: 'none',
  height: '100%',
  '& .MuiDataGrid-columnHeader': {
    padding: '0px 8px',
  },
  '& .MuiDataGrid-columnHeaderTitleContainer': {
    gap: `${String(HEADER_TITLE_GAP_PX)}px`,
  },
  '& .MuiDataGrid-iconButtonContainer': {
    width: `${String(SORT_ICON_WIDTH_PX)}px`,
    flexShrink: 0,
  },
  '& .MuiDataGrid-iconButtonContainer .MuiButtonBase-root': {
    padding: 0,
  },
  '& .MuiDataGrid-columnHeaderTitle': {
    fontWeight: HEADER_FONT_WEIGHT,
    fontSize: HEADER_FONT_SIZE_PX,
  },
  [`& .${WRAPPED_HEADER_CLASS} .MuiDataGrid-columnHeaderTitle`]: {
    whiteSpace: 'normal',
    lineHeight: HEADER_LINE_HEIGHT,
    textAlign: 'right',
  },
  '& .MuiDataGrid-columnSeparator': {
    display: 'none',
  },
  '& .MuiDataGrid-cell': {
    padding: '0px 8px',
  },
};

const HEADER_SX: SxProps<Theme> = {
  pb: 1.5,
  borderBottom: 1,
  borderColor: 'divider',
};

/**
 * ContentContainer pads the tile by 24px on every side. The card section pulls
 * itself back to the tile edges so the divider, the Client list bar and the row
 * separators run edge to edge. The padding itself stays, because the tile width
 * measurement above reads the padded box and removing it in one mode would move
 * the table/cards threshold.
 */
const CARD_SECTION_SX: SxProps<Theme> = { mx: -3, mb: -3, mt: 4 };

const AUM_SX: SxProps<Theme> = { mt: 4 };

const CLIENT_LIST_ACCORDION_SX: SxProps<Theme> = {
  '&:before': { display: 'none' },
};

const CLIENT_LIST_SUMMARY_SX: SxProps<Theme> = {
  flexDirection: 'row-reverse',
  gap: 1,
  px: 3,
  background:
    'linear-gradient(90deg, rgba(214, 222, 226, 0.2) 0%, rgba(214, 222, 226, 0.5) 100%)',
  '& .MuiAccordionSummary-content': {
    justifyContent: 'space-between',
    alignItems: 'center',
  },
};

const CARD_SEARCH_SX: SxProps<Theme> = { py: 2, px: 3 };

const BLOCKING_ERROR_SX: SxProps<Theme> = { mt: 2 };

/**
 * A definite height, because the list sits inside an Accordion whose Collapse
 * measures its content, and a flex-grown grid has no height to measure.
 */
const CARD_ROWS_SX: SxProps<Theme> = { height: WIDGET_GRID_HEIGHT };

const CARD_GRID_SX: SxProps<Theme> = {
  border: 'none',
  height: '100%',
  '& .MuiDataGrid-cell': {
    padding: 0,
  },
  '& .MuiDataGrid-row': {
    borderBottom: 1,
    borderColor: 'divider',
    cursor: 'pointer',
  },
  '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
    outline: 'none',
  },
};

const DashboardClientsGrid = ({
  title,
  container = 'widget',
  isHalfWidth = false,
  onDataLoaded,
}: StandardWidgetProps = {}) => {
  const { t } = useLocalization();
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  const isWidget = container === 'widget';
  const [isTableTooNarrow, setIsTableTooNarrow] = useState(false);
  const isCardList = isHalfWidth || isXs || isTableTooNarrow;
  const authLoaded = useAuthIsLoaded();
  const { showSnackbar } = useGlobalSnackbar();

  const [selectedClient, setSelectedClient] = useState<AdvisorInvestor | null>(
    null,
  );
  const [editUserId, setEditUserId] = useState<number | null>(null);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [typedSearchTerm, setTypedSearchTerm] = useState('');
  const [serverSearchTerm, setServerSearchTerm] = useState('');
  const isSearching = serverSearchTerm !== '';
  /**
   * True from a keystroke until the debounced search commits the typed term.
   * The list still holds the previous term's results in that window, so the
   * grid stays covered until the request for the new term is in flight.
   */
  const isSearchPending = typedSearchTerm !== serverSearchTerm;

  useEffect(() => {
    setTypedSearchTerm('');
    setServerSearchTerm('');
  }, [isCardList]);

  const {
    data: clientsData,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useGetAdvisorInvestorsQuery(authLoaded ? PAGE_CLIENTS_QUERY : skipToken);
  const {
    data: searchData,
    isFetching: isSearchFetching,
    isError: isSearchError,
    refetch: refetchSearch,
  } = useGetAdvisorInvestorsQuery(
    authLoaded && serverSearchTerm
      ? { ...PAGE_CLIENTS_QUERY, searchTerm: serverSearchTerm }
      : skipToken,
  );
  const {
    data: gridColumnsData,
    isFetching: isGridColumnsFetching,
    isError: isGridColumnsError,
    refetch: refetchGridColumns,
  } = useGridColumns(GRID_KEYS.ADVISOR_CLIENTS, { skip: !authLoaded });

  /**
   * The rows the grid renders: the search results while a search is active,
   * the resting page otherwise. Everything derived from the list, the Orion
   * column, the fitted widths and the impersonation check, reads this, so it
   * always describes what is on screen.
   */
  const rows = useMemo(
    () => (serverSearchTerm ? searchData?.Records : clientsData?.Records) ?? [],
    [serverSearchTerm, searchData, clientsData],
  );
  const totalAum = useMemo(() => selectTotalAum(clientsData), [clientsData]);
  const clientCount =
    (serverSearchTerm ? searchData?.TotalLength : clientsData?.TotalLength) ??
    0;
  /**
   * Without the resting page or the column configuration there is nothing to
   * lay out. A failed search is not blocking: the layout and its search bar
   * stay, so the user can change or clear the term instead of only retrying it.
   */
  const hasBlockingError = isError || isGridColumnsError;
  const isGridLoading =
    !authLoaded || isLoading || isFetching || isGridColumnsFetching;

  const retryFailedQueries = useCallback(() => {
    if (isError) void refetch();
    if (isGridColumnsError) void refetchGridColumns();
  }, [isError, isGridColumnsError, refetch, refetchGridColumns]);
  const retrySearch = useCallback(() => {
    void refetchSearch();
  }, [refetchSearch]);

  const showOrionColumn = useMemo(() => hasOrionClient(rows), [rows]);
  const isOrionColumnConfigured = useMemo(
    () =>
      (gridColumnsData?.Records ?? []).some(
        (record) =>
          record.GridColumnDataField === 'LastOrionLogin' && record.IsEnabled,
      ),
    [gridColumnsData?.Records],
  );

  const widgetGridColumns = useMemo(
    () => ({
      Records: selectDashboardClientColumns(
        gridColumnsData?.Records,
        showOrionColumn,
      ),
    }),
    [gridColumnsData?.Records, showOrionColumn],
  );

  // Stable callbacks: useClientColumns and useClientActionsMenu list them in
  // their memo dependencies, and the column fit runs downstream of the
  // columns, so an inline literal would re-measure every row on each render.
  const openEditUser = useCallback((investor: AdvisorInvestor) => {
    setEditUserId(investor.UserId);
    setIsEditOpen(true);
  }, []);
  const refetchAfterDelete = useCallback(() => {
    void refetch();
  }, [refetch]);
  const openClientDetails = useCallback((investor: AdvisorInvestor) => {
    setSelectedClient(investor);
  }, []);

  const {
    MenuForInvestor,
    Accompaniment,
    openMenuForInvestor,
    setViewMode,
    viewMode,
    selectedInvestor,
    impersonationError,
    clearImpersonationError,
  } = useClientActionsMenu({
    hideActions: isCardList
      ? HIDDEN_ROW_ACTIONS
      : [...HIDDEN_ROW_ACTIONS, 'accessInvestor'],
    onEditUser: openEditUser,
    onDeleted: refetchAfterDelete,
  });

  const accessInvestor = useCallback(
    (investor: AdvisorInvestor) => {
      setViewMode('accessInvestor', investor);
    },
    [setViewMode],
  );

  useEffect(() => {
    if (!impersonationError) return;
    showSnackbar(impersonationError);
    clearImpersonationError();
  }, [impersonationError, showSnackbar, clearImpersonationError]);

  useEffect(() => {
    if (!isGridLoading) onDataLoaded?.();
  }, [isGridLoading, onDataLoaded]);

  const columns = useClientColumns({
    gridColumns: widgetGridColumns,
    anyFullControl: rows.some((row) => row.CanImpersonate),
    t,
    selectedGroupingId: undefined,
    selectedGroupingLabel: '',
    groupBy: 'none',
    MenuForInvestor: MenuForInvestor as MenuForInvestorComponent,
    openMenuForInvestor,
    shouldFetchInsights: false,
    onNameClick: openClientDetails,
    onAccessInvestor: accessInvestor,
    isAccessingInvestor: viewMode === 'accessInvestor',
    accessingInvestorId: selectedInvestor?.InvestorId ?? null,
  });

  const textMeasurer = useMemo(
    () => createClientGridTextMeasurer(theme, HEADER_FONT_WEIGHT),
    [theme],
  );

  const sizedColumns = useMemo(
    () =>
      columns.map((column) => {
        const sizing = COLUMN_SIZING[column.field];
        return sizing === undefined ? column : { ...column, ...sizing };
      }),
    [columns],
  );

  const fittedWidths = useMemo(
    () =>
      textMeasurer === null
        ? {}
        : fitClientColumnWidths(sizedColumns, rows, textMeasurer, t),
    [sizedColumns, rows, textMeasurer, t],
  );

  const tableWidths = useMemo(
    () => clientTableWidths(sizedColumns, fittedWidths),
    [sizedColumns, fittedWidths],
  );

  const { ref: widthRef, widthPx } = useWidgetWidthIsBelow(
    isWidget,
    tableWidths.compactPx + TABLE_WIDTH_SLACK_PX,
  );
  const tableLayout = selectClientTableLayout(widthPx, tableWidths);

  useEffect(() => {
    setIsTableTooNarrow(tableLayout === 'cards');
  }, [tableLayout]);

  const widgetColumns = useMemo(
    () =>
      sizedColumns.map((column) => {
        const fitted = fittedWidths[column.field];
        if (fitted === undefined) return column;
        if (!column.flex) return { ...column, width: fitted };
        return tableLayout === 'fitted'
          ? { ...column, minWidth: fitted }
          : column;
      }),
    [sizedColumns, fittedWidths, tableLayout],
  );

  const cardColumn = useDashboardClientCardColumn({
    t,
    columns: widgetColumns,
    openMenuForInvestor,
    MenuForInvestor: MenuForInvestor as MenuForInvestorComponent,
    onSelect: setSelectedClient,
  });

  const searchBar = (
    <SearchBar
      id='dashboard-clients-search'
      label={t('searchByName', 'Search by name')}
      onChange={(value) => {
        setTypedSearchTerm(value.trim());
      }}
      onSearch={setServerSearchTerm}
      minCharacters={0}
    />
  );

  const loadErrorAlert = (onRetry: () => void, sx?: SxProps<Theme>) => (
    <Alert
      severity='error'
      action={
        <Button
          color='inherit'
          size='small'
          onClick={onRetry}
        >
          {t('retry', 'Retry')}
        </Button>
      }
      sx={sx}
    >
      {t('advisorClientsWidget.loadError', 'Error loading clients.')}
    </Alert>
  );

  const grid = (
    <DataGridPremium<AdvisorInvestor>
      key={isCardList ? 'dashboard-clients-cards' : 'dashboard-clients'}
      columns={widgetColumns}
      rows={rows}
      getRowId={(row) => row.InvestorId}
      listView={isCardList}
      {...(isCardList
        ? { listViewColumn: cardColumn, getRowHeight: () => 'auto' as const }
        : { initialState: PINNED_ROW_ACTIONS })}
      loading={isGridLoading || isSearchFetching || isSearchPending}
      columnHeaderHeight={HEADER_HEIGHT_PX}
      showColumnVerticalBorder={false}
      showCellVerticalBorder={false}
      sortingOrder={GRID_SORTING_ORDER}
      hideFooter
      disableColumnFilter
      disableColumnMenu
      disableColumnSelector
      disableRowSelectionOnClick
      rowHeight={44}
      localeText={{
        noRowsLabel: isSearching
          ? t('advisorClientsWidget.noClientsFound', 'No clients found')
          : t('advisorClientsWidget.noClients', 'No clients yet'),
      }}
      sx={isCardList ? CARD_GRID_SX : GRID_SX}
    />
  );

  // What fills the list slot under the search bar.
  const gridSlot = isSearchError ? (
    <Box sx={isCardList ? CARD_SEARCH_SX : undefined}>
      {loadErrorAlert(retrySearch)}
    </Box>
  ) : (
    grid
  );

  return (
    <ContentContainer
      container={container}
      sx={isCardList ? CARD_TILE_SX : TILE_SX}
    >
      {Accompaniment}
      <Box
        ref={widthRef}
        sx={isCardList ? CARD_FILL_SX : FILL_SX}
      >
        <WidgetHeader
          title={title ?? t('Clients')}
          sx={isCardList ? undefined : HEADER_SX}
        />
        {isCardList ? (
          <Box sx={AUM_SX}>
            <Typography
              variant='h6'
              sx={{ mb: 1 }}
            >
              {t('AUM')}
            </Typography>
            <Typography variant='h4'>
              {!isError && totalAum !== null
                ? formatNumberToCurrency(totalAum)
                : '-'}
            </Typography>
          </Box>
        ) : null}
        {hasBlockingError ? (
          loadErrorAlert(retryFailedQueries, BLOCKING_ERROR_SX)
        ) : isCardList ? (
          <Box sx={CARD_SECTION_SX}>
            <Divider />
            <Accordion
              disableGutters
              elevation={0}
              defaultExpanded
              sx={CLIENT_LIST_ACCORDION_SX}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={CLIENT_LIST_SUMMARY_SX}
              >
                <Typography variant='subtitle1'>
                  {t('advisorClientsWidget.clientList', 'Client list')}
                </Typography>
                <Typography
                  variant='body2'
                  color='text.secondary'
                >
                  {clientCount}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                <Box sx={CARD_SEARCH_SX}>{searchBar}</Box>
                <Divider />
                <Box sx={CARD_ROWS_SX}>{gridSlot}</Box>
              </AccordionDetails>
            </Accordion>
          </Box>
        ) : (
          <>
            <Box sx={TABLE_SEARCH_SX}>{searchBar}</Box>
            <Box sx={GRID_WRAPPER_SX}>{gridSlot}</Box>
          </>
        )}
        {!hasBlockingError &&
        !isGridLoading &&
        !isCardList &&
        rows.length > 0 &&
        isOrionColumnConfigured &&
        !showOrionColumn ? (
          <Typography
            variant='caption'
            color='text.secondary'
            sx={{ mt: 1 }}
          >
            {t(
              'advisorClientsWidget.orionColumnHidden',
              'No client in this list has an active Orion integration, so Last Orion Login is hidden. The column appears when at least one client does.',
            )}
          </Typography>
        ) : null}
      </Box>
      {selectedClient ? (
        <ClientDetailsFlyout
          open
          toggleDrawer={(isOpen) => {
            if (!isOpen) setSelectedClient(null);
          }}
          client={selectedClient}
        />
      ) : null}
      <React.Suspense fallback={null}>
        {editUserId !== null && isEditOpen ? (
          <EditInvestorForm
            userId={editUserId}
            open={isEditOpen}
            onClose={() => {
              setIsEditOpen(false);
            }}
          />
        ) : null}
      </React.Suspense>
    </ContentContainer>
  );
};

export default DashboardClientsGrid;
